import React from 'react';
import { motion } from 'framer-motion';
import { TimerIcon, FlagIcon } from 'lucide-react';
interface Lap {
  lapNumber: number;
  splitTime: string;
  cumulativeTime: string;
}
interface CompareData {
  time1: string;
  time2: string;
}
interface StopwatchVisualProps {
  display: string;
  precision?: 'seconds' | 'tenths' | 'hundredths';
  variant?: 'single' | 'laps' | 'compare' | 'decimal';
  laps?: Lap[];
  compare?: CompareData;
  highlight?: 'faster' | 'slower' | null;
  showDial?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
  className?: string;
}
export function StopwatchVisual({
  display,
  precision = 'seconds',
  variant = 'single',
  laps = [],
  compare,
  highlight = null,
  showDial = false,
  label,
  mode = 'stimulus',
  className = ''
}: StopwatchVisualProps) {
  const isCompact = mode === 'stimulus';
  // Helper to parse mm:ss.hh into seconds for the analog dial
  const parseTimeToSeconds = (timeStr: string): number => {
    // Handle decimal variant (e.g., "25,8 s" or "25.8")
    if (variant === 'decimal') {
      const clean = timeStr.replace(/[^0-9.,]/g, '').replace(',', '.');
      return parseFloat(clean) || 0;
    }
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      const mins = parseInt(parts[0], 10) || 0;
      const secsParts = parts[1].split(/[.,]/);
      const secs = parseInt(secsParts[0], 10) || 0;
      const frac = secsParts[1] ? parseFloat(`0.${secsParts[1]}`) : 0;
      return mins * 60 + secs + frac;
    }
    return 0;
  };
  const totalSeconds = parseTimeToSeconds(display);
  // Render the LED digital display
  const renderDigitalDisplay = (timeStr: string, isMain = true) => {
    const textClass = isCompact ? isMain ? 'text-4xl md:text-5xl' : 'text-2xl md:text-3xl' : isMain ? 'text-5xl md:text-7xl' : 'text-3xl md:text-4xl';
    if (variant === 'decimal') {
      return <div className="flex items-baseline gap-2 font-mono font-black text-emerald-400 tracking-wider drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" data-id="element-13005">
          <div className={textClass} data-id="element-13006">{timeStr}</div>
        </div>;
    }
    const parts = timeStr.split(':');
    const mins = parts[0] || '00';
    const secsFull = parts[1] || '00';
    const secsParts = secsFull.split(/[.,]/);
    const secs = secsParts[0] || '00';
    const frac = secsParts[1] || '';
    return <div className="flex items-baseline gap-1 md:gap-2 font-mono font-black text-emerald-400 tracking-wider drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" data-id="element-13007">
        <div className={textClass} data-id="element-13008">{mins}</div>
        <div className={`${textClass} pb-1 md:pb-2 text-emerald-500/80`} data-id="element-13009">:</div>
        <div className={textClass} data-id="element-13010">{secs}</div>
        {precision !== 'seconds' && frac && <>
            <div className={`${textClass} pb-1 md:pb-2 text-emerald-500/80`} data-id="element-13011">
              .
            </div>
            <div className={`${textClass} text-emerald-500/80`} data-id="element-13012">{frac}</div>
          </>}
      </div>;
  };
  // Render the analog sweep dial
  const renderAnalogDial = (seconds: number) => {
    const size = isCompact ? 100 : 140;
    const sweepAngle = seconds % 60 * 6; // 360 degrees / 60 seconds
    return <div className="relative rounded-full bg-white shadow-inner border-4 border-slate-200 flex-shrink-0" style={{
      width: size,
      height: size
    }} data-id="element-13013">
        <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible" data-id="element-13014">
          {/* Ticks */}
          {[...Array(60)].map((_, i) => {
          const isMajor = i % 5 === 0;
          return <line key={i} x1="50" y1={isMajor ? '4' : '6'} x2="50" y2={isMajor ? '10' : '8'} stroke={isMajor ? '#64748b' : '#cbd5e1'} strokeWidth={isMajor ? '1.5' : '1'} transform={`rotate(${i * 6} 50 50)`} data-id="element-13015" />;
        })}
          {/* Sweep Hand */}
          <motion.g initial={{
          rotate: 0
        }} animate={{
          rotate: sweepAngle
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} style={{
          originX: '50px',
          originY: '50px'
        }} data-id="element-13016">
            <line x1="50" y1="50" x2="50" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" data-id="element-13017" />
            <circle cx="50" cy="50" r="3" fill="#ef4444" data-id="element-13018" />
          </motion.g>
        </svg>
      </div>;
  };
  const renderStopwatchBox = (timeStr: string, isMain = true, highlightState: 'faster' | 'slower' | null = null) => {
    const paddingClass = isCompact ? 'p-4' : 'p-6 md:p-8';
    let borderClass = 'border-slate-700';
    let glowClass = '';
    if (highlightState === 'faster') {
      borderClass = 'border-emerald-500';
      glowClass = 'shadow-[0_0_15px_rgba(16,185,129,0.3)]';
    } else if (highlightState === 'slower') {
      borderClass = 'border-rose-500';
      glowClass = 'shadow-[0_0_15px_rgba(244,63,94,0.3)]';
    }
    return <div className={`flex flex-col items-center gap-4 ${glowClass}`} data-id="element-13019">
        <div className={`relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl md:rounded-3xl border-4 ${borderClass} shadow-2xl overflow-hidden ${paddingClass}`} data-id="element-13020">
          <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" data-id="element-13021" />
          <div className="flex items-center gap-6" data-id="element-13022">
            {showDial && renderAnalogDial(parseTimeToSeconds(timeStr))}
            {renderDigitalDisplay(timeStr, isMain)}
          </div>
        </div>
        {highlightState && <div className={`text-sm font-bold uppercase tracking-wider ${highlightState === 'faster' ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-13023">
            {highlightState === 'faster' ? 'Sneller' : 'Langzamer'}
          </div>}
      </div>;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className={`flex flex-col items-center gap-6 ${className}`} data-id="element-13024">
      {variant === 'single' || variant === 'decimal' ? renderStopwatchBox(display) : variant === 'compare' && compare ? <div className="flex flex-col md:flex-row items-center justify-center gap-8" data-id="element-13025">
          {renderStopwatchBox(compare.time1, false, highlight === 'faster' ? 'faster' : highlight === 'slower' ? 'slower' : null)}
          <div className="text-slate-400 font-bold text-xl" data-id="element-13026">VS</div>
          {renderStopwatchBox(compare.time2, false, highlight === 'faster' ? 'slower' : highlight === 'slower' ? 'faster' : null)}
        </div> : variant === 'laps' && laps.length > 0 ? <div className="flex flex-col items-center gap-6 w-full max-w-md" data-id="element-13027">
          {renderStopwatchBox(display)}
          <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-13028">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-13029">
              <div className="flex items-center gap-1" data-id="element-13030">
                <FlagIcon size={14} data-id="element-13031" /> Ronde
              </div>
              <div data-id="element-13032">Tussentijd</div>
              <div data-id="element-13033">Totaal</div>
            </div>
            <div className="divide-y divide-slate-100" data-id="element-13034">
              {laps.map((lap, idx) => <div key={idx} className="px-4 py-3 flex items-center justify-between text-sm md:text-base" data-id="element-13035">
                  <div className="font-bold text-slate-700 w-12" data-id="element-13036">
                    {lap.lapNumber}
                  </div>
                  <div className="font-mono text-slate-600" data-id="element-13037">
                    {lap.splitTime}
                  </div>
                  <div className="font-mono font-bold text-indigo-600" data-id="element-13038">
                    {lap.cumulativeTime}
                  </div>
                </div>)}
            </div>
          </div>
        </div> : null}

      {label && <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-13039">
          <TimerIcon size={18} className="text-slate-500" data-id="element-13040" />
          <p className={`font-semibold text-slate-700 ${isCompact ? 'text-sm' : 'text-base'}`} data-id="element-13041">
            {label}
          </p>
        </div>}
    </motion.div>;
}